
import { Injectable } from "@angular/core";
import { CheckRideRequestCancelTypeAllItems, CheckRideRequestEntity, CheckRideRequestSubStatusAllItems, FlightSchoolApplicantEntity } from "../../../meta-data/app-meta-data.service";
import { deepCopy, DwModalButtonStandardConfigs, DwModalConfig, DwModalResult, DwModalService, DwModalSize } from "@devwareapps/devware-cap";
import { SchoolRepositoryService } from "./school-repository.service";
import { ExaminerRepositoryService } from "../../examiner/services/examiner-repository.service";
import { SchoolSwapStudentRequest } from "../models/school-swap-student-request.model";
import { SchoolSwapStudentModalComponent } from "../components/school-swap-student-modal/school-swap-student-modal.component";
import { Observable, of } from "rxjs";
import { SchoolChangeApplicantSwapType, SchoolSwapStudentResult } from "../models/school-swap-student-result.model";
import { CheckCalendarAvailabilityRequestDTO } from "../../examiner/models/check-calendar-availability-request.model";
import { DateTimeUtilService } from "../../shared/util/date-time-util.service";
import { SchoolCheckrideUtilService } from "./school-checkride-util.service";
import { map } from "rxjs/operators";
import { CheckCalendarAvailabilityResultDTO } from "../../examiner/models/check-calendar-availability-result.model";

@Injectable({
    providedIn: 'root'
})
export class SchoolChangeApplicantUtilService {
    constructor(
        protected schoolRepositoryService: SchoolRepositoryService,
        protected examinerRepositoryService: ExaminerRepositoryService,
        private dateTimeUtilService: DateTimeUtilService,
        private schoolCheckrideUtilService: SchoolCheckrideUtilService
    ) {
    }

    changeStudentApplicant(originalCheckride: CheckRideRequestEntity, newCheckride: CheckRideRequestEntity, swapType: SchoolChangeApplicantSwapType, currentCheckride: CheckRideRequestEntity, newApplicant: FlightSchoolApplicantEntity): Observable<SchoolSwapStudentResult> {
        // Logic
        // If swap type = cancel
        //   1. Cancel existing Checkride
        //   2. Create new Checkride for new applicant - copy certain fields from original checkride and set the applicant data for the new applicant
        // If swap type = swap
        //   1. Update existing Checkride to set new applicant data
        //   2. Update current checkride to set original applicant data

        // Remove any weird fields including configFG and queryMetaDataList
        this.cleanCheckrides(originalCheckride, newCheckride, currentCheckride);

        const originalCheckrideSnapshot = deepCopy(originalCheckride);
        const currentCheckrideSnapshot = currentCheckride ? deepCopy(currentCheckride) : null;
        
        const checkridesToSave: CheckRideRequestEntity[] = [];
        const messages: string[] = [];

        // Cancel original checkride (So we have a record of it)
        this.setCheckrideToCancelled(originalCheckride, newApplicant, `Checkride cancelled due to applicant change to ${newApplicant.ApplicantName}.`, messages);
        checkridesToSave.push(originalCheckride);

        if (currentCheckride) {
            // Cancel current checkride for new applicant
            this.setCheckrideToCancelled(currentCheckride, newApplicant, `Checkride cancelled due to applicant change to a new Checkride.`, messages);
            checkridesToSave.push(currentCheckride);
        }

        // Create new checkride for new applicant (this keeps all of the examiner details, time, etc)
        const newApplicantCheckride = this.setupNewApplicantCheckride(newCheckride, originalCheckrideSnapshot, currentCheckrideSnapshot, newApplicant, messages);
        checkridesToSave.push(newApplicantCheckride);

        switch (swapType) {
            case SchoolChangeApplicantSwapType.swap:
                if (currentCheckrideSnapshot) {
                    // Create a new checkride for the previous applicant (this keeps all of the original date/time details)
                    const previousApplicantCheckride = this.setupPreviousApplicantCheckride(originalCheckrideSnapshot, currentCheckrideSnapshot, newApplicant, messages);
                    checkridesToSave.push(previousApplicantCheckride);
                    break;
                }
        }

        console.log(`changeStudentApplicant - ${messages.join('; ')}`, checkridesToSave);

        return this.schoolRepositoryService.saveCheckrides(checkridesToSave)
            .pipe(map((savedCheckrides: CheckRideRequestEntity[]) => {
                const swapResult = this.mapCheckridResults(savedCheckrides, swapType, originalCheckride, newApplicant, messages);

                console.log('changeStudentApplicant - completed', swapResult);

                return swapResult;
            }));
    }

    cleanCheckrides(...checkrides: CheckRideRequestEntity[]) {

        for(const newCheckride of checkrides) {
            if (newCheckride){

                (newCheckride as any).queryMetaDataList = null;
                (newCheckride as any).contextFG = null;
            }
        }
    }


    mapCheckridResults(savedCheckrides: CheckRideRequestEntity[], swapType: SchoolChangeApplicantSwapType, originalCheckride: CheckRideRequestEntity, newApplicant: FlightSchoolApplicantEntity, messages: string[]): SchoolSwapStudentResult {
        const swapResult: SchoolSwapStudentResult = {
            swapType: swapType,
            cancelledCheckrids: savedCheckrides.filter(cr => cr.CheckRideRequestSubStatusId == CheckRideRequestSubStatusAllItems.Cancelled),
            newApplicantCheckride: savedCheckrides.find(cr => cr.FlightSchoolApplicantId == newApplicant.FlightSchoolApplicantId && cr.CheckRideRequestSubStatusId != CheckRideRequestSubStatusAllItems.Cancelled),
            originalApplicantCheckride: savedCheckrides.find(cr => cr.FlightSchoolApplicantId == originalCheckride.FlightSchoolApplicantId && cr.CheckRideRequestSubStatusId != CheckRideRequestSubStatusAllItems.Cancelled)
        };

        return swapResult;
    }

    setupPreviousApplicantCheckride(originalCheckride: CheckRideRequestEntity, previousCheckrideForNewApplicant: CheckRideRequestEntity, newApplicant: FlightSchoolApplicantEntity, messages: string[]): CheckRideRequestEntity {
        const checkride = deepCopy(originalCheckride);

        // Keep the original applicant details, but move them into the previous applicant's slot.
        checkride.CheckRideRequestId = null;
        checkride.SendApplicantCheckrideEmails = originalCheckride?.SendApplicantCheckrideEmails;
        checkride.SendExaminerCheckrideEmails = previousCheckrideForNewApplicant?.SendExaminerCheckrideEmails;
        checkride.ScheduledDateTime = previousCheckrideForNewApplicant.ScheduledDateTime;
        checkride.ScheduleAirportLocationNotes = previousCheckrideForNewApplicant.ScheduleAirportLocationNotes;
        checkride.ScheduledTimeMinutes = previousCheckrideForNewApplicant.ScheduledTimeMinutes;
        checkride.ScheduledPilotExaminerId = previousCheckrideForNewApplicant.ScheduledPilotExaminerId;
        checkride.ScheduledAirportId = previousCheckrideForNewApplicant.ScheduledAirportId;
        checkride.ScheduledCheckRideScenarioId = previousCheckrideForNewApplicant.ScheduledCheckRideScenarioId;
        checkride.ScheduledPilotExaminerLocationId = previousCheckrideForNewApplicant.ScheduledPilotExaminerLocationId;
        checkride.FlightSchoolExaminerId = previousCheckrideForNewApplicant.FlightSchoolExaminerId;
        checkride.CheckRideExaminerTypeId = previousCheckrideForNewApplicant.CheckRideExaminerTypeId;

        checkride.CheckRideResult = null;
        checkride.PlanOfAction = null;

        messages.push(`Swapping current applicant CheckrideId ${previousCheckrideForNewApplicant?.CheckRideRequestId}`);

        return checkride;
    }

    setupNewApplicantCheckride(newCheckride: CheckRideRequestEntity, originalCheckride: CheckRideRequestEntity, currentCheckrideForNewApplicant: CheckRideRequestEntity, newApplicant: FlightSchoolApplicantEntity, messages: string[]): CheckRideRequestEntity {
        newCheckride.CheckRideRequestId = null;
        newCheckride.SendApplicantCheckrideEmails = currentCheckrideForNewApplicant?.SendApplicantCheckrideEmails ?? originalCheckride?.SendApplicantCheckrideEmails;
        newCheckride.SendExaminerCheckrideEmails = originalCheckride?.SendExaminerCheckrideEmails;

        const applicantCheckrideUpdate = this.schoolCheckrideUtilService.updateCheckrideApplicantData(newCheckride, newApplicant, false);

        newCheckride = { ...newCheckride, ...applicantCheckrideUpdate };

        newCheckride.ExaminerPrivateNotes = '';

        newCheckride.FlightSchoolPrivateNotes += `\n Checkride created due to applicant change. Original Checkride Id: ${originalCheckride?.CheckRideRequestId}.`;

        messages.push(`Swapping current applicant into new applicants previous checkride with original date/time details.`);

        messages.push(`Creating checkride for new applicant ${newApplicant?.ApplicantName}.`);

        return newCheckride;
    }

    setCheckrideToCancelled(checkride: CheckRideRequestEntity, newApplicant: FlightSchoolApplicantEntity, notes: string, messages: string[]) {
        checkride.CheckRideRequestSubStatusId = CheckRideRequestSubStatusAllItems.Cancelled;
        checkride.CheckRideCancellationTypeId = CheckRideRequestCancelTypeAllItems.Other;
        checkride.CancellationNotes = notes;

        messages.push(`Cancelling checkride ${checkride?.CheckRideRequestId}.`);
    }


    checkNewCheckrideAvailability(originalCheckride: CheckRideRequestEntity, checkride: CheckRideRequestEntity): Observable<CheckCalendarAvailabilityResultDTO> {

        const request: CheckCalendarAvailabilityRequestDTO = {
            CheckRideDateTime: this.dateTimeUtilService.convertDateTimeToJson(checkride.ScheduledDateTime),
            DurationMinutes: checkride.ScheduledTimeMinutes,
            CheckRideRequestId: originalCheckride?.CheckRideRequestId || checkride?.CheckRideRequestId,
            PilotExaminerId: checkride.ScheduledPilotExaminerId,
            FlightSchoolExaminerId: checkride.FlightSchoolExaminerId,
            CheckRideExaminerTypeId: checkride.CheckRideExaminerTypeId
        }

        return this.examinerRepositoryService.checkCalendarAvailability(request);
    }
}
